import { Menu } from 'antd';
import { type ItemType } from 'antd/es/menu/interface';
import { selectedMenuKeysSelector } from 'features/app-ui/selectors';
import { setSelectedMenuKeys } from 'features/app-ui/slice';
import { useGetCurrentUserAclGroupQuery } from 'features/user/query';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from 'shared/components/AppMainNavigationSideBar/AppMainNavigationSideBar.module.scss';
import { menuItems } from 'shared/components/AppMainNavigationSideBar/constants';
import {
  APP_MODULE_CODE,
  type TMenuItem,
} from 'shared/components/AppMainNavigationSideBar/types';

export const AppMainNavigationSideBar = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const selectedMenuKeys = useAppSelector(selectedMenuKeysSelector);

  const { data } = useGetCurrentUserAclGroupQuery();

  const filteredMenuItems = useMemo(() => {
    if (!data?.modules) {
      return menuItems.filter(
        item =>
          item.code === APP_MODULE_CODE.HOME ||
          item.code === APP_MODULE_CODE.ACCOUNTING ||
          item.code === APP_MODULE_CODE.HR
      );
    }

    return menuItems
      .map(moduleItem => {
        if (
          moduleItem.code === APP_MODULE_CODE.HOME ||
          moduleItem.code === APP_MODULE_CODE.ACCOUNTING ||
          moduleItem.code === APP_MODULE_CODE.HR
        ) {
          return moduleItem;
        }

        const aclModule = data.modules.find(m => m.code === moduleItem.code);
        if (!aclModule) return null;

        if (moduleItem.children && moduleItem.children.length > 0) {
          const filteredChildren = moduleItem.children.filter(childItem => {
            const aclMenu = aclModule.menus.find(
              m => m.code === childItem.code
            );
            return aclMenu?.status === true;
          });

          if (filteredChildren.length > 0) {
            return {
              ...moduleItem,
              children: filteredChildren,
            };
          }
          return null;
        }

        return moduleItem;
      })
      .filter(Boolean) as TMenuItem[];
  }, [data?.modules]);

  const handleClickMenuItem = useCallback(
    ({ key }: { key: string }) => {
      if (key.startsWith('/')) navigate(key);
    },
    [navigate]
  );

  const checkMatchPath = useCallback(() => {
    let selectedChildKey = pathname;

    const findMatch = (items: TMenuItem[], parentKeys: string[]) => {
      for (const item of items) {
        if (
          item.key.startsWith('/') &&
          (item.key === pathname || pathname.startsWith(`${item.key}/`))
        ) {
          selectedChildKey = item.key;
          return true;
        }
        if (item.children && item.children.length > 0) {
          if (findMatch(item.children, [...parentKeys, item.key])) {
            return true;
          }
        }
      }
      return false;
    };

    findMatch(filteredMenuItems, []);

    dispatch(setSelectedMenuKeys([selectedChildKey]));
  }, [dispatch, pathname, filteredMenuItems]);

  useEffect(() => {
    checkMatchPath();
  }, [checkMatchPath, filteredMenuItems]);

  return (
    <Menu
      mode="vertical"
      theme="dark"
      className={styles.sideBarMenu}
      subMenuOpenDelay={0.1}
      subMenuCloseDelay={0.15}
      items={filteredMenuItems as ItemType[]}
      onClick={handleClickMenuItem}
      selectedKeys={selectedMenuKeys}
    />
  );
});
